/** Minimal shape of the Web Speech API recognition object we rely on. */
export interface RecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  onresult: ((event: { resultIndex: number; results: ArrayLike<{ isFinal: boolean; 0: { transcript: string } }> }) => void) | null;
  onerror: ((event: { error: string }) => void) | null;
  onend: (() => void) | null;
  start(track?: unknown): void;
  stop(): void;
  abort(): void;
}

export type RecognitionConstructor = new () => RecognitionLike;

/** Returns the constructor from a window-like object: SpeechRecognition, else webkitSpeechRecognition, else null. */
export function findRecognition(win: Record<string, unknown> | undefined): RecognitionConstructor | null {
  if (!win) return null;
  const standard = win['SpeechRecognition'];
  if (typeof standard === 'function') return standard as RecognitionConstructor;
  const prefixed = win['webkitSpeechRecognition'];
  if (typeof prefixed === 'function') return prefixed as RecognitionConstructor;
  return null;
}

export interface MicOption { deviceId: string; label: string }

/** Microphones from enumerateDevices output, "System default" first; unnamed ones are numbered. */
export function micOptions(devices: ReadonlyArray<{ kind: string; deviceId: string; label: string }>): MicOption[] {
  const inputs = devices.filter((d) => d.kind === 'audioinput' && d.deviceId !== 'communications');
  const hasDefault = inputs.some((d) => d.deviceId === 'default');
  const others = inputs.filter((d) => d.deviceId !== 'default');
  return [
    { deviceId: hasDefault ? 'default' : '', label: 'System default' },
    ...others.map((d, index) => ({ deviceId: d.deviceId, label: d.label.trim() || `Microphone ${index + 1}` })),
  ];
}

/** An opened microphone track that must be released after use. */
export interface TrackLike { stop(): void }

const SENTENCE_END = /[.!?]$|[.!?] $|\n$/;

/** Append dictated text to existing text, applying spacing, capitalisation and spoken-command rules. */
export function appendDictation(existing: string, dictated: string): string {
  const text = dictated.trim();
  if (!text) return existing;
  const command = text.toLowerCase();
  if (command === 'new line') return `${existing}\n`;
  if (command === 'new paragraph') return `${existing}\n\n`;
  let prefix = existing;
  if (existing && !/\s$/.test(existing)) prefix += ' ';
  let body = text;
  if (!existing || SENTENCE_END.test(existing)) body = body.charAt(0).toUpperCase() + body.slice(1);
  return prefix + body;
}

export type DictationState = 'idle' | 'listening' | 'error';

export interface DictationHandlers {
  onFinal(text: string): void;
  onInterim(text: string): void;
  onState(state: DictationState, message?: string): void;
}

/** User-facing messages for recognition error codes. */
export function dictationErrorMessage(code: string): string {
  switch (code) {
    case 'not-allowed':
    case 'service-not-allowed':
      return 'Microphone permission was denied.';
    case 'no-speech':
      return 'No speech was detected.';
    case 'audio-capture':
      return 'No microphone was found.';
    case 'network':
      return 'Speech recognition needs a network connection.';
    default:
      return 'Dictation stopped unexpectedly.';
  }
}

export interface DictationController {
  readonly supported: boolean;
  start(): void;
  stop(): void;
  dispose(): void;
}

/** Wrap one recognition instance (created lazily on first start) with dictation event handling. */
export function createDictation(
  ctor: RecognitionConstructor | null,
  handlers: DictationHandlers,
  options?: { lang?: string; getTrack?: () => Promise<TrackLike | null> },
): DictationController {
  if (!ctor) {
    return { supported: false, start() {}, stop() {}, dispose() {} };
  }
  const Recognition = ctor;
  let recognition: RecognitionLike | null = null;
  let listening = false;
  let disposed = false;
  let lastState: DictationState | null = null;
  let starting = false;
  let track: TrackLike | null = null;

  function releaseTrack(): void {
    track?.stop();
    track = null;
  }

  function emit(state: DictationState, message?: string): void {
    lastState = state;
    handlers.onState(state, message);
  }

  function ensureRecognition(): RecognitionLike {
    if (recognition) return recognition;
    const instance = new Recognition();
    instance.lang = options?.lang ?? 'en-US';
    instance.continuous = true;
    instance.interimResults = true;
    instance.onresult = (event) => {
      let interim = '';
      let sawFinal = false;
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        if (!result) continue;
        const transcript = result[0].transcript;
        if (result.isFinal) {
          sawFinal = true;
          handlers.onFinal(transcript);
          handlers.onInterim('');
        } else {
          interim += transcript;
        }
      }
      if (!sawFinal && interim) handlers.onInterim(interim);
    };
    instance.onerror = (event) => {
      if (event.error === 'aborted') return;
      emit('error', dictationErrorMessage(event.error));
    };
    instance.onend = () => {
      listening = false;
      releaseTrack();
      if (lastState !== 'error') emit('idle');
    };
    recognition = instance;
    return instance;
  }

  function begin(opened: TrackLike | null): void {
    const instance = ensureRecognition();
    try {
      if (opened) {
        track = opened;
        try {
          instance.start(opened);
        } catch (error) {
          // Browsers that cannot take a track throw TypeError; use the default microphone.
          if (!(error instanceof TypeError)) throw error;
          releaseTrack();
          instance.start();
        }
      } else {
        instance.start();
      }
      listening = true;
      emit('listening');
    } catch {
      releaseTrack();
      emit('error', 'Dictation could not start.');
    }
  }

  return {
    supported: true,
    start() {
      if (disposed || listening || starting) return;
      if (!options?.getTrack) {
        begin(null);
        return;
      }
      starting = true;
      options.getTrack().then(
        (opened) => {
          starting = false;
          if (disposed) { opened?.stop(); return; }
          begin(opened);
        },
        () => {
          starting = false;
          if (!disposed) emit('error', 'The selected microphone could not be opened.');
        },
      );
    },
    stop() {
      if (!recognition || !listening) return;
      listening = false;
      recognition.stop();
    },
    dispose() {
      if (disposed) return;
      disposed = true;
      listening = false;
      if (recognition) {
        recognition.onresult = null;
        recognition.onerror = null;
        recognition.onend = null;
        recognition.abort();
        recognition = null;
      }
      releaseTrack();
    },
  };
}

/** One of the voices the machine has installed. */
export interface VoiceLike {
  readonly lang: string;
  readonly name: string;
  readonly voiceURI?: string;
  readonly default?: boolean;
  readonly localService?: boolean;
}

/**
 * Minimal shape of speechSynthesis for read-aloud.
 *
 * Everything past `speaking` is optional: a browser that lacks it still reads
 * aloud, and a test can hand in a two-method stand-in.
 */
export interface SynthesisLike {
  speak(utterance: unknown): void;
  cancel(): void;
  speaking: boolean;
  /** Browsers park the queue after a cancel; read-aloud has to let it go again. */
  paused?: boolean;
  resume?(): void;
  getVoices?(): ReadonlyArray<VoiceLike>;
  addEventListener?(type: 'voiceschanged', listener: () => void): void;
  removeEventListener?(type: 'voiceschanged', listener: () => void): void;
}

export type UtteranceConstructor = new (text: string) => {
  lang: string;
  rate: number;
  voice?: VoiceLike | null;
};

const SPOKEN_LIMIT = 1000;

/**
 * What a reply sounds like when it is read rather than looked at.
 *
 * The desk writes for the eye - fenced code, bullets, a middle dot between a
 * label and its detail - and a voice reading those out loud says "asterisk"
 * and "hash" where a person would say nothing. This takes the marks off and
 * leaves the words.
 */
export function spokenText(raw: string): string {
  return raw
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/`([^`]*)`/g, '$1')
    .replace(/!\[[^\]]*\]\([^)]*\)/g, ' ')
    .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/^[ \t]{0,3}#{1,6}[ \t]+/gm, '')
    .replace(/^[ \t]*[-*+][ \t]+/gm, '')
    .replace(/^[ \t]*>[ \t]?/gm, '')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/\*([^*]+)\*/g, '$1')
    .replace(/(^|[\s(])__([^_]+)__(?=[\s).,!?]|$)/g, '$1$2')
    .replace(/(^|[\s(])_([^_]+)_(?=[\s).,!?]|$)/g, '$1$2')
    .replace(/~~([^~]+)~~/g, '$1')
    .replace(/[·•|]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** A reply that can be read aloud. */
export interface SpeakableReply {
  readonly id: string;
  readonly text: string;
}

/**
 * The replies that have not been read yet, oldest first.
 *
 * Anything already read stays read, so a refresh that hands the same rows back
 * does not say them twice, and a reply with nothing left to say once the marks
 * are off is left out rather than read as silence.
 */
export function unspoken(
  replies: readonly SpeakableReply[],
  already: ReadonlySet<string>,
): SpeakableReply[] {
  const seen = new Set<string>();
  const fresh: SpeakableReply[] = [];
  for (const reply of replies) {
    if (already.has(reply.id) || seen.has(reply.id)) continue;
    seen.add(reply.id);
    if (spokenText(reply.text) === '') continue;
    fresh.push(reply);
  }
  return fresh;
}

/** One passage of speech from several replies, each ended so the voice pauses between them. */
export function speechFor(replies: readonly SpeakableReply[]): string {
  return replies
    .map((reply) => spokenText(reply.text).replace(/[.\s]+$/, ''))
    .filter((text) => text !== '')
    .join('. ');
}

/** Cut long text at a word boundary so a reply is not read for minutes. */
function clip(text: string): string {
  if (text.length <= SPOKEN_LIMIT) return text;
  const head = text.slice(0, SPOKEN_LIMIT);
  const cut = head.lastIndexOf(' ');
  return cut > 0 ? head.slice(0, cut) : head;
}

/** Stable identity used by the voice picker and persisted preference. */
export function voiceId(voice: VoiceLike): string {
  return voice.voiceURI?.trim() || voice.name;
}

const NATURAL_VOICE_HINT = /(natural|neural|enhanced|premium|siri)/i;

/**
 * The installed voice closest to the wanted language.
 *
 * An explicit owner choice always wins. Without one, prefer a same-language
 * local/default voice and give voices whose platform name advertises a
 * higher-quality/natural variant a small bonus. This is only ranking installed
 * system voices; it never pretends browser TTS is a GPT Live voice.
 */
export function pickVoice(
  voices: ReadonlyArray<VoiceLike>,
  lang?: string,
  preferredVoiceId?: string | null,
): VoiceLike | null {
  if (voices.length === 0) return null;
  const preferred = preferredVoiceId?.trim();
  if (preferred) {
    const chosen = voices.find((voice) => voiceId(voice) === preferred);
    if (chosen) return chosen;
  }

  const wanted = (lang ?? '').toLowerCase().replace('_', '-');
  const base = wanted.split('-')[0] ?? '';
  const scored = voices.map((voice, index) => {
    const voiceLang = voice.lang.toLowerCase().replace('_', '-');
    const voiceBase = voiceLang.split('-')[0] ?? '';
    let score = 0;
    if (wanted !== '' && voiceLang === wanted) score += 100;
    else if (base !== '' && voiceBase === base) score += 60;
    if (NATURAL_VOICE_HINT.test(voice.name)) score += 12;
    if (voice.localService === true) score += 4;
    if (voice.default === true) score += 2;
    return { voice, score, index };
  });
  scored.sort((a, b) => b.score - a.score || a.index - b.index);
  return scored[0]?.voice ?? null;
}

/**
 * Run something once the machine's voices are loaded.
 *
 * A browser hands back an empty voice list until it has read them off the
 * system, and anything queued before that is dropped without a sound - which
 * is exactly what "I turned it on and nothing happened" looks like. This waits
 * for the list, and gives up waiting rather than staying silent for ever.
 */
export function whenVoicesReady(
  synthesis: SynthesisLike | null,
  run: () => void,
  waitMs = 500,
): void {
  if (!synthesis || typeof synthesis.getVoices !== 'function') { run(); return; }
  if (synthesis.getVoices().length > 0) { run(); return; }
  if (typeof synthesis.addEventListener !== 'function') { run(); return; }
  let done = false;
  const go = (): void => {
    if (done) return;
    done = true;
    synthesis.removeEventListener?.('voiceschanged', go);
    run();
  };
  synthesis.addEventListener('voiceschanged', go);
  setTimeout(go, waitMs);
}

/** Utterances are held so the browser cannot collect one mid-sentence and cut the voice off. */
const inFlight: unknown[] = [];

/**
 * Speak text aloud, after cancelling whatever is being said.
 *
 * The utterance is kept in hand, a voice is chosen rather than left to the
 * language tag alone, and the queue is let go again afterwards - a cancel
 * leaves it parked, and a parked queue says nothing.
 */
export function speak(
  synthesis: SynthesisLike | null,
  Utterance: UtteranceConstructor | null,
  text: string,
  lang?: string,
  preferredVoiceId?: string | null,
): boolean {
  if (!synthesis || !Utterance) return false;
  const content = clip(spokenText(text));
  if (content === '') return false;
  const utterance = new Utterance(content);
  if (lang) utterance.lang = lang;
  const voice = pickVoice(synthesis.getVoices?.() ?? [], lang, preferredVoiceId);
  if (voice) utterance.voice = voice;
  inFlight.push(utterance);
  if (inFlight.length > 4) inFlight.shift();
  synthesis.cancel();
  synthesis.speak(utterance);
  if (synthesis.paused === true) synthesis.resume?.();
  return true;
}

/** Stop reading at once and drop what was held. */
export function silence(synthesis: SynthesisLike | null): void {
  inFlight.length = 0;
  synthesis?.cancel();
}
